import { useMemo } from 'react';
import { useParams } from 'react-router';
import { Edit, useForm } from '@refinedev/antd';
import { useList, HttpError } from '@refinedev/core';
import { Form, Input, Select } from 'antd';
import type { Conference, Role, User } from '@viq-admin/types';
import { ROLE_OPTIONS } from '@viq-admin/ui';
import { RequireCan } from '../../components/RequireCan';

/**
 * Edit a user: name / role / conferences ONLY. There is deliberately NO
 * company field — `companyId` is immutable (moving a user between tenants is a
 * hard product non-goal; deactivate + recreate instead). Email is read-only
 * and company is shown read-only for context.
 *
 * `initialValues` come from the edit read (`query.data.data`, the Refine v6
 * useForm query shape), seeding role/conferences so an unchanged field still
 * appears in the update body. Because `companyId`/`email` are rendered as
 * name-less display Inputs, they never enter the submitted body. `manage User`
 * gates the whole page.
 *
 * Refine v6 `useList` returns `{ query, result }`; conference options read
 * `result.data`.
 */
type UserRow = User & { companyName?: string; conferenceIds?: string[] };
interface UpdateUserInput {
  name?: string;
  role?: Role;
  conferenceIds?: string[];
}

export function UserEdit() {
  // The route uses `:id` (App.tsx resource table); the plan's detail snippet
  // uses `:oid`. Resolve either so the id passes to `useForm` explicitly —
  // Refine only auto-derives it when the resource is registered, which the
  // test harness's bare <Refine> is not.
  const { id, oid } = useParams<{ id?: string; oid?: string }>();
  const userId = id ?? oid;
  const { formProps, saveButtonProps, query } = useForm<
    UserRow,
    HttpError,
    UpdateUserInput
  >({ resource: 'users', action: 'edit', id: userId, meta: { idField: 'oid' } });
  const record = query?.data?.data;
  // Only the user's company's granted conferences are offered (dataProvider
  // maps `meta.companyId` to `?companyId=` and `meta.archived` to
  // `?archived=true`). Archiving keeps the grant, so archived conferences of
  // the company count as granted too.
  const companyId = record?.companyId;
  const { result, query: activeQuery } = useList<Conference>({
    resource: 'conferences',
    meta: { companyId: companyId ?? null },
    queryOptions: { enabled: !!companyId },
  });
  const { result: archivedResult, query: archivedQuery } = useList<Conference>({
    resource: 'conferences',
    meta: { companyId: companyId ?? null, archived: true },
    queryOptions: { enabled: !!companyId },
  });
  const grantsLoaded =
    !!companyId && !!activeQuery?.isSuccess && !!archivedQuery?.isSuccess;
  const watched = Form.useWatch('conferenceIds', formProps.form) as
    | string[]
    | undefined;
  const currentIds = useMemo(
    () => watched ?? record?.conferenceIds ?? [],
    [watched, record],
  );

  const active = useMemo(
    () => (grantsLoaded ? (result?.data ?? []) : []),
    [grantsLoaded, result],
  );
  const archived = useMemo(
    () => (grantsLoaded ? (archivedResult?.data ?? []) : []),
    [grantsLoaded, archivedResult],
  );
  // Ungranted ids are derived from the field's CURRENT value, so removing one
  // makes it disappear for good. Empty until the grants have loaded.
  const legacyIds = useMemo(() => {
    if (!grantsLoaded) return [];
    const grantedIds = new Set([...active, ...archived].map((c) => c.id));
    return currentIds.filter((id) => !grantedIds.has(id));
  }, [grantsLoaded, active, archived, currentIds]);
  // Names for ungranted assignments come from the cross-tenant lists
  // (active + archived), fetched only when there is something to resolve.
  const lookupEnabled = legacyIds.length > 0;
  const { result: allResult } = useList<Conference>({
    resource: 'conferences',
    meta: { companyId: null },
    queryOptions: { enabled: lookupEnabled },
  });
  const { result: allArchivedResult } = useList<Conference>({
    resource: 'conferences',
    meta: { companyId: null, archived: true },
    queryOptions: { enabled: lookupEnabled },
  });

  const conferenceOptions = useMemo(() => {
    const all = [...(allResult?.data ?? []), ...(allArchivedResult?.data ?? [])];
    const keep = new Set([...currentIds, ...(record?.conferenceIds ?? [])]);
    return [
      ...active.map((c) => ({ label: c.name, value: c.id })),
      // Archived conferences are only surfaced when already assigned.
      ...archived
        .filter((c) => keep.has(c.id))
        .map((c) => ({ label: `${c.name} (archived)`, value: c.id })),
      ...legacyIds.map((id) => ({
        label: `${all.find((c) => c.id === id)?.name ?? id} (not granted to this company)`,
        value: id,
      })),
    ];
  }, [active, archived, legacyIds, allResult, allArchivedResult, currentIds, record]);

  return (
    <RequireCan I="manage" a="User">
      <Edit saveButtonProps={saveButtonProps}>
        <Form {...formProps} layout="vertical" initialValues={record}>
          <Form.Item label="Email">
            <Input value={record?.email} disabled />
          </Form.Item>
          <Form.Item label="Company">
            <Input value={record?.companyName ?? record?.companyId} disabled />
          </Form.Item>
          <Form.Item label="Name" name="name">
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item label="Role" name="role">
            <Select options={ROLE_OPTIONS} />
          </Form.Item>
          <Form.Item label="Conferences" name="conferenceIds">
            <Select mode="multiple" options={conferenceOptions} />
          </Form.Item>
        </Form>
      </Edit>
    </RequireCan>
  );
}
